<template>
  <Container>
    <DxGridWithSearch
      title="Lab Locations"
      gridName="labLocations"
      :columnChooser="true"
      :dataSource="dataSource"
      keyExpr="labLocationId"
      :columns="columns"
      @initialized="initGrid"
      :toolbar="toolbar"
    >
      <div slot="actions-cell" class="d-flex align-items-center" slot-scope="{ data }">
        <IconButton
          @click="editLabLocation(data)"
          class="mx-1 pointer text-primary p-0"
          v-tooltip.left="'Edit lab location'"
          icon="pen-alt"
        />
      </div>
      <template v-slot:extraActions>
        <div class="d-flex align-items-center">
          <AddButton @click="addLabLocation" />
        </div>
      </template>
    </DxGridWithSearch>
    <Modal :status="isEditing" @close="isEditing = false">
      <div class="edit-popup-content">
        <h2>{{ selectedLabLocation.labLocationId ? "Edit" : "Add" }} Lab Location</h2>
        <TextInput
          label="Name"
          v-model="selectedLabLocation.name"
          :validator="$v.selectedLabLocation.name"
        />
        <TextInput label="Address 1" v-model="selectedLabLocation.address1" />
        <TextInput label="Address 2" v-model="selectedLabLocation.address2" />
        <div class="row">
          <TextInput label="City" class="col-4" v-model="selectedLabLocation.city" />
          <TextInput label="State" class="col-4" v-model="selectedLabLocation.state" />
          <NumberInput label="Zip Code" class="col-4" v-model="selectedLabLocation.zipCode" />
        </div>
        <div class="row">
          <TextInput label="Phone" class="col-4" v-model="selectedLabLocation.phone" />
          <SelectInput
            class="col-8"
            label="Time Zone"
            v-model="selectedLabLocation.timeZoneId"
            :items="timeZones"
            :validator="$v.selectedLabLocation.timeZoneId"
            :showClearButton="false"
          />
        </div>
        <TextAreaInput label="Report Text" v-model="selectedLabLocation.reportText" />
        <SubmitCancelRow @cancel="handleClose" @submit="handleSubmit" />
      </div>
    </Modal>
  </Container>
</template>

<script>
import DataSource from "devextreme/data/data_source";
import Container from "./common/Container.vue";
import DxGridWithSearch from "./common/DxGridWithSearch.vue";
import { SettingsApi } from "@/services";
import IconButton from "./common/IconButton.vue";
import Modal from "./common/Modal.vue";
import TextInput from "./common/TextInput.vue";
import NumberInput from "./common/NumberInput.vue";
import SubmitCancelRow from "./common/SubmitCancelRow.vue";
import TextAreaInput from "./TextAreaInput.vue";
import SelectInput from "./common/SelectInput.vue";
import { mapState } from "vuex";
import { handleErrors } from "@/modules/handleErrors";
import { cloneDeep } from "lodash";
import AddButton from "./common/AddButton.vue";
import { required } from "vuelidate/lib/validators";

export default {
  components: {
    Container,
    DxGridWithSearch,
    IconButton,
    Modal,
    TextInput,
    NumberInput,
    SubmitCancelRow,
    TextAreaInput,
    SelectInput,
    AddButton
  },
  data() {
    return {
      selectedLabLocation: null,
      isEditing: false,

      grid: {}
    };
  },
  computed: {
    ...mapState({
      timeZones: state => state.dropdowns.timeZones,
      currentLab: state => state.currentLab,
      currentUser: state => state.currentUser
    }),
    defaultLabLocation() {
      return {
        name: "",
        address1: "",
        address2: "",
        city: "",
        state: "",
        zipCode: null,
        phone: "",
        reportText: "",
        timeZoneId: this.currentUser.timeZoneId,
        labId: this.currentLab
      };
    },
    dataSource() {
      return new DataSource({ store: SettingsApi.labLocationsSearch, key: "labLocationId" });
    },
    columns() {
      return [
        { dataField: "name", dataType: "string", allowSorting: false },
        { dataField: "address1", caption: "Address 1", dataType: "string", allowSorting: false },
        { dataField: "address2", caption: "Address 2", dataType: "string", allowSorting: false },
        { dataField: "city", dataType: "string", allowSorting: false },
        { dataField: "state", dataType: "string", allowSorting: false },
        { dataField: "zipCode", dataType: "number", allowSorting: false },
        { dataField: "phone", dataType: "string", allowSorting: false },
        {
          type: "buttons",
          caption: "Actions",
          cellTemplate: "actions-cell"
        }
      ];
    },
    toolbar() {
      return {
        items: [
          {
            location: "after",
            template: "extraActions"
          }
        ]
      };
    }
  },
  mounted() {
    this.$store.dispatch("dropdowns/getTimeZones");
    this.selectedLabLocation = this.defaultLabLocation;
  },
  validations() {
    return {
      selectedLabLocation: {
        name: {
          required
        },
        timeZoneId: {
          required
        }
      }
    };
  },
  methods: {
    initGrid({ component }) {
      this.grid = component;
    },
    addLabLocation() {
      this.isEditing = true;
      this.selectedLabLocation = this.defaultLabLocation;
    },
    editLabLocation({ data }) {
      this.selectedLabLocation = cloneDeep(data);
      this.isEditing = true;
    },
    handleClose() {
      this.isEditing = false;
      this.selectedLabLocation = this.defaultLabLocation;
    },
    async handleSubmit() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        await window.notify("Please verify your input.", "warning");
        return;
      }
      try {
        if (this.selectedLabLocation?.labLocationId) {
          await SettingsApi.updateLabLocation(this.selectedLabLocation);
          window.notify("Lab location updated.");
        } else {
          await SettingsApi.createLabLocation(this.selectedLabLocation);
          window.notify("Lab location created.");
        }
        this.handleClose();
        this.grid.refresh();
      } catch (error) {
        handleErrors(error);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.edit-popup-content {
  width: 97%;
}
</style>
